// src/components/HeaderActions.tsx - RESPONSIVE VERSION
/**
 * Header Actions Component - Responsive Version
 * 
 * Features:
 * - Always visible: Zoom controls and secondary line toggle (essential for classroom use)
 * - Mobile menu: All other actions hidden behind burger menu on narrow screens
 * - Responsive breakpoint: 1024px (lg breakpoint)
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Settings, Download, FolderOpen, Maximize, Minimize, CircleHelp } from 'lucide-react';
import { RootState, defaultNumberFormatConfig, NumberFormatConfig } from '../types/redux';
import { exportToPNGFixed, exportViewportToPNG } from '../lib/export';
import { 
  toggleSecondaryLine, 
  setScaleFactor, 
  setSecondaryNumberType,
  toggleHideAllLabels,
  setFormatType as setSecondaryFormatType,
  updateNumberFormatProperty as updateSecondaryNumberFormatProperty,
  setInheritFormatFromPrimary,
} from '../store/slices/secondaryLineSlice';
import {
  updateNumberFormatProperty as updatePrimaryNumberFormatProperty,
  setFormatType as setPrimaryFormatType,
  setNumberType,
} from '../store/slices/numberLinesSlice';
import { clearVisibilityOverrides } from '../store/slices/labelVisibilitySlice';
import { toggleConstants, toggleConstantVisibility, setVisibleConstants } from '../store/slices/constantsSlice';
import { setScale, toggleOrientation } from '../store/slices/viewportSlice';
import { clearJumps } from '../store/slices/jumpIndicatorsSlice';
import { clearAnnotations } from '../store/slices/annotationsSlice';
import { clearAllMeasurements } from '../store/slices/uiSlice';
import { clearAllExpressions } from '../store/slices/expressionsSlice';
import { clearCustomTicks } from '../store/slices/customTicksSlice';
import { MATHEMATICAL_CONSTANTS } from '../lib/constants';
import { useDialog, useSidePanel } from '../components/ui';
import { ShareDialogContent } from '../components/ShareDialogContent';
import { LoadDialogContent } from '../components/LoadDialogContent';
import { PreferencesContent } from '../components/PreferencesContent';
import { AboutDialogContent } from '../components/AboutDialogContent';
import { HelpDialogContent } from '../components/HelpDialogContent';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import ExportDialog from './ExportDialog';
import { DNLToolsPanel } from '../components/dnl';
import NumberFormatSelector from './NumberFormatSelector';

interface HeaderActionsProps {
  className?: string;
  numberLineRef?: React.RefObject<HTMLDivElement>;
}

export const HeaderActions: React.FC<HeaderActionsProps> = ({ className = '', numberLineRef }) => {
  const dispatch = useDispatch();
  
  // UI framework hooks
  const { openDialog } = useDialog();
  const { openSidePanel, closeSidePanel } = useSidePanel();
  
  // Safe state access with fallbacks
  const preferences = useSelector((state: RootState) => state.preferences || { theme: 'light' });
  const secondaryLine = useSelector((state: RootState) => state.secondaryLine || { enabled: false });
  const numberLine = useSelector((state: RootState) => state.numberLines || { numberType: 'integer', fractionDisplay: { format: 'mixed', simplify: true } });
  const constants = useSelector((state: RootState) => state.constants || { enabled: false });
  const viewport = useSelector((state: RootState) => state.viewport || { scale: 100 });
  const isEquivalentMode = useSelector(
    (state: RootState) => state.equivalentFractions?.mode === 'equivalentFractions'
  );
  const isVertical = viewport.orientation === 'vertical';
  
  // Local state
  const [showConstantsDropdown, setShowConstantsDropdown] = useState(false);
  const [showSecondaryLineDropdown, setShowSecondaryLineDropdown] = useState(false);
  const [showMainLineDropdown, setShowMainLineDropdown] = useState(false);
  const [showFileDropdown, setShowFileDropdown] = useState(false);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDNLToolsOpen, setIsDNLToolsOpen] = useState(false);
  const [showFormatSettingsDialog, setShowFormatSettingsDialog] = useState(false);
  const [showPrimaryFormatSettingsDialog, setShowPrimaryFormatSettingsDialog] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  // Track window width for responsive behavior
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track fullscreen state changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const isNarrow = windowWidth < 1024; // Show burger menu on screens narrower than 1024px

  // Number format helpers
  const mapLegacyTypeToFormat = (legacy?: 'integer' | 'decimal' | 'fraction' | 'percent'): NumberFormatConfig['type'] => {
    if (!legacy) return 'integer';
    if (legacy === 'percent') return 'percentage';
    return legacy;
  };

  const formatTypeLabel = (type: NumberFormatConfig['type']) => {
    switch (type) {
      case 'decimal': return 'Decimals';
      case 'fraction': return 'Fractions';
      case 'percentage': return 'Percentages';
      case 'currency': return 'Currency';
      case 'custom': return 'Units';
      default: return 'Integers';
    }
  };

  const primaryFormat = numberLine.numberFormat || defaultNumberFormatConfig;
  const primaryFormatType = primaryFormat.type || mapLegacyTypeToFormat(numberLine.numberType);

  const secondaryStoredFormat = secondaryLine.numberFormat || defaultNumberFormatConfig;
  const secondaryStoredFormatType = secondaryStoredFormat.type || mapLegacyTypeToFormat(secondaryLine.numberType);
  const secondaryFormat = secondaryLine.inheritFormatFromPrimary ? primaryFormat : secondaryStoredFormat;
  const secondaryFormatType = secondaryLine.inheritFormatFromPrimary ? primaryFormatType : secondaryStoredFormatType;

  const primaryFractionFormat = primaryFormat.fractionFormat || numberLine.fractionDisplay?.format || 'mixed';
  const primaryFractionSimplify = 
    primaryFormat.fractionSimplify ?? numberLine.fractionDisplay?.simplify ?? true;

  const secondaryFractionFormat = secondaryFormat.fractionFormat || secondaryLine.fractionDisplay?.format || 'mixed';
  const secondaryFractionSimplify = 
    secondaryFormat.fractionSimplify ?? secondaryLine.fractionDisplay?.simplify ?? true;

  const handlePrimaryFormatSelection = (type: NumberFormatConfig['type']) => {
    dispatch(setPrimaryFormatType(type));
    const legacyMap: Record<NumberFormatConfig['type'], 'integer' | 'decimal' | 'fraction' | 'percent' | null> = {
      integer: 'integer',
      decimal: 'decimal',
      fraction: 'fraction',
      percentage: 'percent',
      currency: null,
      custom: null,
    };
    const legacyType = legacyMap[type];
    if (legacyType) {
      dispatch(setNumberType(legacyType));
    }
  };

  const handleSecondaryFormatSelection = (type: NumberFormatConfig['type']) => {
    dispatch(setSecondaryFormatType(type));
    const legacyMap: Record<NumberFormatConfig['type'], 'integer' | 'decimal' | 'fraction' | 'percent' | null> = {
      integer: 'integer',
      decimal: 'decimal',
      fraction: 'fraction',
      percentage: 'percent',
      currency: null,
      custom: null,
    };
    const legacyType = legacyMap[type];
    if (legacyType) {
      dispatch(setSecondaryNumberType(legacyType));
    }
  };

  const handlePrimaryFractionFormatChange = (format: 'mixed' | 'improper') => {
    dispatch(updatePrimaryNumberFormatProperty({ key: 'fractionFormat', value: format }));
  };

  const handleSecondaryFractionFormatChange = (format: 'mixed' | 'improper') => {
    if (secondaryLine.inheritFormatFromPrimary) return;
    dispatch(updateSecondaryNumberFormatProperty({ key: 'fractionFormat', value: format }));
  };

  const handlePrimaryFractionSimplifyChange = (value: boolean) => {
    dispatch(updatePrimaryNumberFormatProperty({ key: 'fractionSimplify', value }));
  };

  const handleSecondaryFractionSimplifyChange = (value: boolean) => {
    if (secondaryLine.inheritFormatFromPrimary) return;
    dispatch(updateSecondaryNumberFormatProperty({ key: 'fractionSimplify', value }));
  };

  const handleSecondaryInheritanceToggle = (value: boolean) => {
    dispatch(setInheritFormatFromPrimary(value));
  };

  // Zoom handlers
  // Zoom handlers - extended range with adaptive step size
  const handleZoomIn = useCallback(() => {
    // Use larger multiplier at extreme zoom levels for more responsive feel
    const multiplier = viewport.scale > 1000 ? 1.5 : 1.2;
    const newScale = Math.min(10000, viewport.scale * multiplier);
    dispatch(setScale(newScale));
  }, [dispatch, viewport.scale]);

  const handleZoomOut = useCallback(() => {
    // Use larger divisor at extreme zoom levels for more responsive feel
    const divisor = viewport.scale < 1 ? 1.5 : 1.2;
    const newScale = Math.max(0.1, viewport.scale / divisor);
    dispatch(setScale(newScale));
  }, [dispatch, viewport.scale]);

  const handleZoomReset = useCallback(() => {
    dispatch(setScale(83)); // Reset to default scale (83px)
  }, [dispatch]);

  // Fullscreen toggle handler
  const handleToggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  }, []);

  // Orientation toggle (session only; leaves preference untouched)
  const handleToggleOrientation = useCallback(() => {
    dispatch(toggleOrientation());
  }, [dispatch]);

  // Keyboard shortcuts
  const shortcuts = useKeyboardShortcuts();

  // Override zoom shortcuts with current scale
  useEffect(() => {
    if (shortcuts['ctrl+=']) {
      shortcuts['ctrl+='] = handleZoomIn;
    }
    if (shortcuts['ctrl+-']) {
      shortcuts['ctrl+-'] = handleZoomOut;
    }
    if (shortcuts['ctrl+0']) {
      shortcuts['ctrl+0'] = handleZoomReset;
    }
  }, [shortcuts, handleZoomIn, handleZoomOut, handleZoomReset]);

  // Action handlers using UI framework
  const handleShare = useCallback(() => {
    try {
      openDialog(<ShareDialogContent />, "Share Number Line", { size: 'md' });
    } catch (error) {
      console.error('Share error:', error);
    }
  }, [openDialog]);

  const handleLoad = useCallback(() => {
    try {
      openDialog(<LoadDialogContent />, "Load Shared Scene", { size: 'md' });
    } catch (error) {
      console.error('Load error:', error);
    }
  }, [openDialog]);

  const handleSettings = useCallback(() => {
    try {
      openSidePanel(<PreferencesContent />, "Preferences");
    } catch (error) {
      console.error('Settings error:', error);
    }
  }, [openSidePanel]);

  const handleAbout = useCallback(() => {
    try {
      openDialog(<AboutDialogContent />, "About Number Line", { size: 'md' });
    } catch (error) {
      console.error('About error:', error);
    }
  }, [openDialog]);

  const handleHelp = useCallback(() => {
    try {
      openDialog(<HelpDialogContent />, "Help & Tutorials", { size: 'lg' });
    } catch (error) {
      console.error('Help error:', error);
    }
  }, [openDialog]);

  const toggleMobileMenu = useCallback(() => {
    setShowMobileMenu(prev => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setShowMobileMenu(false);
  }, []);

  const handleResetAll = useCallback(() => {
    // Clear all canvas content while leaving user preferences/settings intact
    dispatch(clearAnnotations());
    dispatch(clearJumps());
    dispatch(clearAllMeasurements());
    dispatch(clearAllExpressions());
    dispatch(clearCustomTicks());
    setShowResetConfirm(false);
  }, [dispatch]);

  const toggleConstantsDropdown = useCallback(() => {
    setShowConstantsDropdown(prev => !prev);
  }, []);

  const closeConstantsDropdown = useCallback(() => {
    setShowConstantsDropdown(false);
  }, []);

  const handleToggleConstant = useCallback((constantId: string) => {
    dispatch(toggleConstantVisibility(constantId));
  }, [dispatch]);

  const handleShowAllConstants = useCallback(() => {
    const allConstantIds = MATHEMATICAL_CONSTANTS.map(c => c.id);
    dispatch(setVisibleConstants(allConstantIds));
  }, [dispatch]);

  const handleHideAllConstants = useCallback(() => {
    dispatch(setVisibleConstants([]));
  }, [dispatch]);

  // Main line handlers
  const toggleMainLineDropdown = useCallback(() => {
    setShowMainLineDropdown(prev => !prev);
  }, []);

  const closeMainLineDropdown = useCallback(() => {
    setShowMainLineDropdown(false);
  }, []);

  // Secondary line handlers
  const toggleSecondaryLineDropdown = useCallback(() => {
    setShowSecondaryLineDropdown(prev => !prev);
  }, []);

  const closeSecondaryLineDropdown = useCallback(() => {
    setShowSecondaryLineDropdown(false);
  }, []);

  const handleScaleFactorChange = useCallback((numerator: number, denominator: number) => {
    dispatch(setScaleFactor({ n: numerator, d: denominator }));
  }, [dispatch]);

  // File dropdown handlers
  const toggleFileDropdown = useCallback(() => {
    setShowFileDropdown(prev => !prev);
  }, []);

  const closeFileDropdown = useCallback(() => {
    setShowFileDropdown(false);
  }, []);

  // Settings dropdown handlers
  const toggleSettingsDropdown = useCallback(() => {
    setShowSettingsDropdown(prev => !prev);
  }, []);

  const closeSettingsDropdown = useCallback(() => {
    setShowSettingsDropdown(false);
  }, []);

  // DNL Tools toggle handler
  const handleToggleDNLTools = useCallback(() => {
    if (isDNLToolsOpen) {
      closeSidePanel();
      setIsDNLToolsOpen(false);
    } else {
      // Create a wrapper component that tracks when the panel closes
      const DNLToolsWrapper = () => {
        useEffect(() => {
          return () => {
            // This cleanup function runs when the component unmounts (panel closes)
            setIsDNLToolsOpen(false);
          };
        }, []);
        return <DNLToolsPanel isDark={preferences.theme === 'dark'} />;
      };
      openSidePanel(<DNLToolsWrapper />, "DNL Tools");
      setIsDNLToolsOpen(true);
    }
  }, [isDNLToolsOpen, openSidePanel, closeSidePanel, preferences.theme]);

  // Export handler (accepts optional background for ExportDialog compatibility)
  const handleExport = useCallback(async (_background?: string) => {
    if (!numberLineRef?.current || isExporting) return;

    const container = numberLineRef.current;
    const filename = `number-line-export.png`;
    const isEquivalentFractions = container.hasAttribute('data-equivalent-fractions-container');

    try {
      setIsExporting(true);

      if (isEquivalentFractions) {
        await exportViewportToPNG(container, { filename, background: 'white', scale: 2 });
        console.log('Equivalent fractions export completed successfully');
        return;
      }

      const svgElement = container.querySelector('.number-line-svg') as SVGSVGElement | null;
      if (svgElement) {
        await exportToPNGFixed(svgElement, filename, 2);
        console.log('Export completed successfully');
        return;
      }

      // Fallback if SVG is not found
      await exportViewportToPNG(container, { filename, background: 'white', scale: 2 });
      console.log('Export completed via fallback');
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setIsExporting(false);
    }
  }, [numberLineRef, isExporting]);

  return (
    <div className={`header-actions flex items-center gap-2 ${className}`}>
      {/* Always Visible: Zoom Controls and Secondary Line Toggle */}
      <div className="flex items-center gap-1">
        {/* Zoom Controls - Always visible for classroom use */}
        <button
          onClick={handleZoomOut}
          className={`px-2 py-1.5 text-xs rounded transition-colors flex items-center gap-1 ${
            preferences.theme === 'dark'
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
          title="Zoom out (Ctrl+-)"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
          </svg>
          <span className="hidden sm:inline text-xs">Out</span>
        </button>

        <button
          onClick={handleZoomReset}
          className={`px-2 py-1.5 text-xs rounded transition-colors flex items-center gap-1 w-[76px] justify-center ${
            preferences.theme === 'dark'
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
          title="Reset zoom to default (Ctrl+0)"
        >
          <span className="font-mono text-xs">
            {viewport.scale >= 1000 
              ? `${(viewport.scale / 1000).toFixed(1)}K` 
              : viewport.scale >= 1 
                ? Math.round(viewport.scale) 
                : viewport.scale.toFixed(2)}px
          </span>
          <span className="hidden sm:inline text-xs">Reset</span>
        </button>

        <button
          onClick={handleZoomIn}
          className={`px-2 py-1.5 text-xs rounded transition-colors flex items-center gap-1 ${
            preferences.theme === 'dark'
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
          title="Zoom in (Ctrl+=)"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
          </svg>
          <span className="hidden sm:inline text-xs">In</span>
        </button>

        {/* Fullscreen Toggle */}
        <button
          onClick={handleToggleFullscreen}
          className={`px-2 py-1.5 text-xs rounded transition-colors flex items-center gap-1 ${
            isFullscreen
              ? preferences.theme === 'dark'
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-blue-500 text-white hover:bg-blue-600'
              : preferences.theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
          title={isFullscreen ? "Exit fullscreen (Esc)" : "Enter fullscreen (F11)"}
        >
          {isFullscreen ? (
            <Minimize className="w-4 h-4" />
          ) : (
            <Maximize className="w-4 h-4" />
          )}
        </button>

        {/* Orientation Toggle (session only) */}
        <button
          onClick={handleToggleOrientation}
          className={`px-2 py-1.5 text-xs rounded transition-colors flex items-center gap-1 ${
            preferences.theme === 'dark'
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
          title={`Switch to ${isVertical ? 'horizontal' : 'vertical'} orientation (session only)`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12h16M12 4v16" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7l-2-3-2 3M15 17l2 3 2-3M7 9l-3 2 3 2M17 15l3-2-3-2" />
          </svg>
          <span className="hidden sm:inline text-xs">{isVertical ? 'Vertical' : 'Horizontal'}</span>
        </button>
      </div>

      {!isEquivalentMode && (
        <>
          {/* Separator */}
          <div className={`w-px h-6 mx-1 ${
            preferences.theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'
          }`} />

          {/* Main Line Label Settings */}
          <div className="relative">
            <button
              onClick={toggleMainLineDropdown}
              className={`px-2 py-1.5 text-sm rounded transition-colors flex items-center gap-1 ${
                preferences.theme === 'dark'
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
              title="Main number line label settings"
            >
              <span className="hidden sm:inline">Labels</span>
              <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d={showMainLineDropdown ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
              </svg>
            </button>

            {/* Main Line Dropdown */}
            {showMainLineDropdown && (
              <div className={`absolute top-full right-0 mt-1 p-2 rounded-lg shadow-lg border z-50 min-w-48 ${
                preferences.theme === 'dark'
                  ? 'bg-gray-800 border-gray-600 text-white'
                  : 'bg-white border-gray-200 text-gray-900'
              }`}>
                <div className="text-xs font-medium mb-2">Main Number Line Labels</div>

                <div className="mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Display Format</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400">
                      {formatTypeLabel(primaryFormatType)}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handlePrimaryFormatSelection('decimal')}
                      className={`flex-1 text-left px-2 py-1 rounded text-xs flex items-center justify-center gap-2 transition-colors ${
                        primaryFormatType === 'decimal'
                          ? preferences.theme === 'dark'
                            ? 'bg-blue-600 text-white'
                            : 'bg-blue-500 text-white'
                          : preferences.theme === 'dark'
                            ? 'text-gray-300 hover:bg-gray-700'
                            : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="font-mono">0.5</span>
                      Decimals
                    </button>
                    <button
                      onClick={() => handlePrimaryFormatSelection('fraction')}
                      className={`flex-1 text-left px-2 py-1 rounded text-xs flex items-center justify-center gap-2 transition-colors ${
                        primaryFormatType === 'fraction'
                          ? preferences.theme === 'dark'
                            ? 'bg-blue-600 text-white'
                            : 'bg-blue-500 text-white'
                          : preferences.theme === 'dark'
                            ? 'text-gray-300 hover:bg-gray-700'
                            : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="font-mono">½</span>
                      Fractions
                    </button>
                  </div>

                  {primaryFormatType === 'fraction' && (
                    <div className="mt-2 flex flex-col gap-1">
                      <button
                        onClick={() => handlePrimaryFractionFormatChange(primaryFractionFormat === 'mixed' ? 'improper' : 'mixed')}
                        className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                          preferences.theme === 'dark'
                            ? 'text-gray-300 hover:bg-gray-700'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <span className={`w-4 h-4 rounded border flex items-center justify-center text-xs ${
                          primaryFractionFormat === 'mixed'
                            ? 'bg-blue-500 border-blue-500 text-white'
                            : preferences.theme === 'dark'
                              ? 'border-gray-500'
                              : 'border-gray-400'
                        }`}>
                          {primaryFractionFormat === 'mixed' && '✓'}
                        </span>
                        Mixed Numbers (1½ vs ³⁄₂)
                      </button>
                      <button
                        onClick={() => handlePrimaryFractionSimplifyChange(!primaryFractionSimplify)}
                        className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                          preferences.theme === 'dark'
                            ? 'text-gray-300 hover:bg-gray-700'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <span className={`w-4 h-4 rounded border flex items-center justify-center text-xs ${
                          primaryFractionSimplify
                            ? 'bg-blue-500 border-blue-500 text-white'
                            : preferences.theme === 'dark'
                              ? 'border-gray-500'
                              : 'border-gray-400'
                        }`}>
                          {primaryFractionSimplify && '✓'}
                        </span>
                        Simplify (½ vs ²⁄₄)
                      </button>
                    </div>
                  )}

                  <div className="mt-2 border-t border-gray-300 dark:border-gray-600 pt-2">
                    <button
                      onClick={() => {
                        setShowPrimaryFormatSettingsDialog(true);
                        setShowMainLineDropdown(false);
                      }}
                      className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                        preferences.theme === 'dark'
                          ? 'bg-gray-700 text-white hover:bg-gray-600'
                          : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                      }`}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                              d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                      Open Format Settings
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {!isEquivalentMode && (
      <>
      {/* Secondary Line Toggle - Always visible for classroom use */}
      <div className="flex items-center gap-1">
        <div className="relative">
          <button
            onClick={toggleSecondaryLineDropdown}
            className={`px-2 py-1.5 text-sm rounded transition-colors flex items-center gap-1 ${
              secondaryLine.enabled
                ? preferences.theme === 'dark'
                  ? 'bg-green-600 text-white'
                  : 'bg-green-500 text-white'
                : preferences.theme === 'dark'
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
            title="Toggle secondary number line"
          >
            <span className="hidden sm:inline">Double</span>
            <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d={showSecondaryLineDropdown ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
            </svg>
          </button>

        {/* Secondary Line Dropdown */}
        {showSecondaryLineDropdown && (
          <div className={`absolute top-full right-0 mt-1 p-2 rounded-lg shadow-lg border z-50 min-w-48 ${
            preferences.theme === 'dark'
              ? 'bg-gray-800 border-gray-600 text-white'
              : 'bg-white border-gray-200 text-gray-900'
          }`}>
            <div className="text-xs font-medium mb-2">Secondary Line</div>
            
            {/* Toggle Button */}
            <button
              onClick={() => { dispatch(toggleSecondaryLine()); closeSecondaryLineDropdown(); }}
              className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                secondaryLine.enabled
                  ? preferences.theme === 'dark'
                    ? 'bg-green-600 text-white'
                    : 'bg-green-500 text-white'
                  : preferences.theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-700'
                    : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {secondaryLine.enabled ? 'Disable' : 'Enable'} Secondary Line
            </button>

            {/* Hide All Labels Toggle */}
            <button
              onClick={() => {
                // 1️⃣ flip the global flag
                dispatch(toggleHideAllLabels());
                // 2️⃣ wipe any stale per‑label overrides so the new global setting
                //    is the single source of truth
                dispatch(clearVisibilityOverrides());
                closeSecondaryLineDropdown(); 
              }}
              className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                secondaryLine.hideAllLabels
                  ? preferences.theme === 'dark'
                    ? 'bg-red-600 text-white'
                    : 'bg-red-500 text-white'
                  : preferences.theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-700'
                    : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M6 18L18 6M6 6l12 12" />
              </svg>
              {secondaryLine.hideAllLabels ? 'Show All Labels' : 'Hide All Labels'}
            </button>

            {/* Display Format Section */}
            <div className="mt-2 pt-2 border-t border-gray-300 dark:border-gray-600">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs text-gray-500 dark:text-gray-400">Display Format</div>
                <label className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                  <input
                    type="checkbox"
                    checked={!!secondaryLine.inheritFormatFromPrimary}
                    onChange={(e) => handleSecondaryInheritanceToggle(e.target.checked)}
                    className="rounded border-gray-400 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                  <span>Inherit main</span>
                </label>
              </div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                {secondaryLine.inheritFormatFromPrimary
                  ? `Using ${formatTypeLabel(primaryFormatType)} from main line`
                  : `Secondary uses ${formatTypeLabel(secondaryFormatType)}`}
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => handleSecondaryFormatSelection('decimal')}
                  disabled={secondaryLine.inheritFormatFromPrimary}
                  className={`flex-1 text-left px-2 py-1 rounded text-xs flex items-center justify-center gap-2 transition-colors ${
                    secondaryFormatType === 'decimal'
                      ? preferences.theme === 'dark'
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-500 text-white'
                      : preferences.theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-600 hover:bg-gray-100'
                  } ${secondaryLine.inheritFormatFromPrimary ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <span className="font-mono">0.5</span>
                  Decimals
                </button>
                <button
                  onClick={() => handleSecondaryFormatSelection('fraction')}
                  disabled={secondaryLine.inheritFormatFromPrimary}
                  className={`flex-1 text-left px-2 py-1 rounded text-xs flex items-center justify-center gap-2 transition-colors ${
                    secondaryFormatType === 'fraction'
                      ? preferences.theme === 'dark'
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-500 text-white'
                      : preferences.theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-600 hover:bg-gray-100'
                  } ${secondaryLine.inheritFormatFromPrimary ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <span className="font-mono">½</span>
                  Fractions
                </button>
              </div>

              {secondaryFormatType === 'fraction' && (
                <div className={`mt-2 flex flex-col gap-1 ${secondaryLine.inheritFormatFromPrimary ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  <button
                    onClick={() => handleSecondaryFractionFormatChange(secondaryFractionFormat === 'mixed' ? 'improper' : 'mixed')}
                    disabled={secondaryLine.inheritFormatFromPrimary}
                    className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                      preferences.theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center text-xs ${
                      secondaryFractionFormat === 'mixed'
                        ? 'bg-blue-500 border-blue-500 text-white'
                        : preferences.theme === 'dark'
                          ? 'border-gray-500'
                          : 'border-gray-400'
                    }`}>
                      {secondaryFractionFormat === 'mixed' && '✓'}
                    </span>
                    Mixed Numbers (1½ vs ³⁄₂)
                  </button>
                  <button
                    onClick={() => handleSecondaryFractionSimplifyChange(!secondaryFractionSimplify)}
                    disabled={secondaryLine.inheritFormatFromPrimary}
                    className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                      preferences.theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center text-xs ${
                      secondaryFractionSimplify
                        ? 'bg-blue-500 border-blue-500 text-white'
                        : preferences.theme === 'dark'
                          ? 'border-gray-500'
                          : 'border-gray-400'
                    }`}>
                      {secondaryFractionSimplify && '✓'}
                    </span>
                    Simplify (½ vs ²⁄₄)
                  </button>
                </div>
              )}

              <div className="mt-2 border-t border-gray-300 dark:border-gray-600 pt-2">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Configure currency, units, percentages, and detailed decimal/fraction options.
                </div>
                <button
                  onClick={() => {
                    setShowFormatSettingsDialog(true);
                    closeSecondaryLineDropdown();
                  }}
                  className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                    preferences.theme === 'dark'
                      ? 'bg-gray-700 text-white hover:bg-gray-600'
                      : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                  } ${secondaryLine.inheritFormatFromPrimary ? 'opacity-60 cursor-not-allowed' : ''}`}
                  disabled={!secondaryLine.enabled || secondaryLine.inheritFormatFromPrimary}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                  Open Format Settings
                </button>
              </div>
            </div>

            {/* Scale Factor Input */}
            <div className="mt-2 pt-2 border-t border-gray-300 dark:border-gray-600">
              <div className="text-sm font-medium mb-2">Scale Factor</div>
              <div className="flex flex-col items-center justify-center gap-2">
                {/* Numerator with Plus/Minus Buttons */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleScaleFactorChange(Math.max(1, secondaryLine.scaleFactor.n - 1), secondaryLine.scaleFactor.d)}
                    className="w-8 h-8 flex items-center justify-center text-lg font-bold rounded bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                    title="Decrease numerator"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    value={secondaryLine.scaleFactor.n}
                    onChange={(e) => handleScaleFactorChange(parseInt(e.target.value) || 1, secondaryLine.scaleFactor.d)}
                    className="w-16 px-2 py-2 text-base text-center border rounded bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={() => handleScaleFactorChange(secondaryLine.scaleFactor.n + 1, secondaryLine.scaleFactor.d)}
                    className="w-8 h-8 flex items-center justify-center text-lg font-bold rounded bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                    title="Increase numerator"
                  >
                    +
                  </button>
                </div>
                {/* Fraction Line */}
                <div className="w-12 h-0.5 bg-gray-400 dark:bg-gray-500"></div>
                {/* Denominator with Plus/Minus Buttons */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleScaleFactorChange(secondaryLine.scaleFactor.n, Math.max(1, secondaryLine.scaleFactor.d - 1))}
                    className="w-8 h-8 flex items-center justify-center text-lg font-bold rounded bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                    title="Decrease denominator"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    value={secondaryLine.scaleFactor.d}
                    onChange={(e) => handleScaleFactorChange(secondaryLine.scaleFactor.n, parseInt(e.target.value) || 1)}
                    className="w-16 px-2 py-2 text-base text-center border rounded bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={() => handleScaleFactorChange(secondaryLine.scaleFactor.n, secondaryLine.scaleFactor.d + 1)}
                    className="w-8 h-8 flex items-center justify-center text-lg font-bold rounded bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                    title="Increase denominator"
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="text-base font-medium text-gray-600 dark:text-gray-300 mt-2 text-center">
                {(secondaryLine.scaleFactor.n / secondaryLine.scaleFactor.d) % 1 === 0 
                  ? Math.round(secondaryLine.scaleFactor.n / secondaryLine.scaleFactor.d).toString()
                  : (secondaryLine.scaleFactor.n / secondaryLine.scaleFactor.d).toFixed(3).replace(/\.?0+$/, '')
                }
              </div>
            </div>

          </div>
        )}
        </div>

        {/* DNL Tools Toggle Button */}
        <button
          onClick={handleToggleDNLTools}
          className={`px-2 py-1 text-sm rounded transition-colors flex items-center gap-1 ${
            isDNLToolsOpen
              ? preferences.theme === 'dark'
                ? 'bg-blue-600 text-white'
                : 'bg-blue-500 text-white'
              : preferences.theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
          title="Toggle DNL Tools sidebar"
        >
          <span className="hidden sm:inline text-[11px]">DNL Tools</span>
        </button>
      </div>
      </>
      )}

      {/* Mobile Menu Button - Only show on narrow screens */}
      {isNarrow && (
        <>
          {/* Separator */}
          <div className={`w-px h-6 mx-1 ${
            preferences.theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'
          }`} />

          <div className="relative">
            <button
              onClick={toggleMobileMenu}
              className={`px-2 py-1.5 text-sm rounded transition-colors flex items-center gap-1 ${
                preferences.theme === 'dark'
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
              title="More options"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Mobile Menu Dropdown */}
            {showMobileMenu && (
              <div className={`absolute top-full right-0 mt-1 p-2 rounded-lg shadow-lg border z-50 min-w-48 ${
                preferences.theme === 'dark'
                  ? 'bg-gray-800 border-gray-600 text-white'
                  : 'bg-white border-gray-200 text-gray-900'
              }`}>
                <div className="text-xs font-medium mb-2">More Options</div>
                
                {/* Share */}
                <button
                  onClick={() => { handleShare(); closeMobileMenu(); }}
                  className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                    preferences.theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                  Share
                </button>

                {/* Load */}
                <button
                  onClick={() => { handleLoad(); closeMobileMenu(); }}
                  className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                    preferences.theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  Load
                </button>

                {/* Export functionality disabled - hidden for now */}
                {/* Export */}
                {/* {numberLineRef && (
                  <button
                    onClick={() => { closeMobileMenu(); }}
                    className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                      preferences.theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export
                  </button>
                )} */}

                {/* Constants */}
                <button
                  onClick={() => { toggleConstantsDropdown(); closeMobileMenu(); }}
                  className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                    constants.visibleConstants.length > 0
                      ? preferences.theme === 'dark'
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-500 text-white'
                      : preferences.theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <span className="font-mono">π</span>
                  Constants
                </button>

                {/* Orientation (mobile) */}
                <button
                  onClick={() => { handleToggleOrientation(); closeMobileMenu(); }}
                  className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                    preferences.theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12h16M12 4v16" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7l-2-3-2 3M15 17l2 3 2-3M7 9l-3 2 3 2M17 15l3-2-3-2" />
                  </svg>
                  {isVertical ? 'Vertical' : 'Horizontal'}
                </button>

                {/* Fullscreen (mobile) */}
                <button
                  onClick={() => { handleToggleFullscreen(); closeMobileMenu(); }}
                  className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                    isFullscreen
                      ? preferences.theme === 'dark'
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-500 text-white'
                      : preferences.theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {isFullscreen ? (
                    <Minimize className="w-3 h-3" />
                  ) : (
                    <Maximize className="w-3 h-3" />
                  )}
                  {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                </button>

                {/* Reset All (mobile) */}
                <button
                  onClick={() => { setShowResetConfirm(true); closeMobileMenu(); }}
                  className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                    preferences.theme === 'dark'
                      ? 'text-red-300 hover:bg-red-900/40'
                      : 'text-red-600 hover:bg-red-50'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m-7 0V5a2 2 0 012-2h3a2 2 0 012 2v2"
                    />
                  </svg>
                  Reset All
                </button>

                {/* Settings */}
                <button
                  onClick={() => { handleSettings(); closeMobileMenu(); }}
                  className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                    preferences.theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Settings className="w-3 h-3" />
                  Settings
                </button>

                {/* Help */}
                <button
                  onClick={() => { handleHelp(); closeMobileMenu(); }}
                  className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                    preferences.theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <CircleHelp className="w-3 h-3" />
                  Help
                </button>

                {/* About */}
                <button
                  onClick={() => { handleAbout(); closeMobileMenu(); }}
                  className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                    preferences.theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  About
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Desktop Actions - Only show on wide screens */}
      {!isNarrow && (
        <>
          {/* Separator */}
          <div className={`w-px h-6 mx-1 ${
            preferences.theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'
          }`} />

          {/* Help Button */}
          <button
            onClick={handleHelp}
            className={`px-2 py-1.5 text-sm rounded transition-colors flex items-center gap-1 ${
              preferences.theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
            title="Help & Tutorials"
          >
            <CircleHelp className="w-4 h-4" />
            <span className="hidden sm:inline">Help</span>
          </button>

          {/* File Dropdown - Combines Share, Load, Export */}
          <div className="relative">
            <button
              onClick={toggleFileDropdown}
              className={`px-2 py-1.5 text-sm rounded transition-colors flex items-center gap-1 ${
                preferences.theme === 'dark'
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
              title="Share, Load, or Export"
            >
              <FolderOpen className="w-4 h-4" />
              <span className="hidden sm:inline">File</span>
              <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d={showFileDropdown ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
              </svg>
            </button>

            {/* File Dropdown Menu */}
            {showFileDropdown && (
              <div className={`absolute top-full right-0 mt-1 p-2 rounded-lg shadow-lg border z-50 min-w-48 ${
                preferences.theme === 'dark'
                  ? 'bg-gray-800 border-gray-600 text-white'
                  : 'bg-white border-gray-200 text-gray-900'
              }`}>
                <div className="text-xs font-medium mb-2">File Operations</div>
                
                {/* Share */}
                <button
                  onClick={() => { handleShare(); closeFileDropdown(); }}
                  className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 transition-colors ${
                    preferences.theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                  Share
                </button>

                {/* Load */}
                <button
                  onClick={() => { handleLoad(); closeFileDropdown(); }}
                  className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 transition-colors ${
                    preferences.theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  Load
                </button>

                {/* Export functionality disabled - hidden for now */}
                {/* Divider */}
                {/* <div className={`my-2 border-t ${
                  preferences.theme === 'dark' ? 'border-gray-600' : 'border-gray-200'
                }`} /> */}

                {/* Export PNG */}
                {/* <button
                  onClick={() => { handleExport(); closeFileDropdown(); }}
                  disabled={!numberLineRef?.current || isExporting}
                  className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 transition-colors ${
                    !numberLineRef?.current || isExporting
                      ? 'opacity-50 cursor-not-allowed'
                      : preferences.theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Download className="w-4 h-4" />
                  {isExporting ? 'Exporting...' : 'Export PNG'}
                </button> */}

                {/* Export Options */}
                {/* <button
                  onClick={() => { setShowExportDialog(true); closeFileDropdown(); }}
                  disabled={!numberLineRef?.current}
                  className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 transition-colors ${
                    !numberLineRef?.current
                      ? 'opacity-50 cursor-not-allowed'
                      : preferences.theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Settings className="w-4 h-4" />
                  Export Options...
                </button> */}
              </div>
            )}
          </div>

          {/* Separator */}
          <div className={`w-px h-6 mx-1 ${
            preferences.theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'
          }`} />

          {/* Constants Dropdown */}
          <div className="relative">
            <button
              onClick={toggleConstantsDropdown}
              className={`px-2 py-1.5 text-sm rounded transition-colors flex items-center gap-1 ${
                constants.visibleConstants.length > 0
                  ? preferences.theme === 'dark'
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-500 text-white'
                  : preferences.theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
              title="Toggle mathematical constants"
            >
              <span className="font-mono text-sm">π</span>
              <span className="hidden sm:inline">Constants</span>
              <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d={showConstantsDropdown ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
              </svg>
            </button>

            {/* Constants Dropdown */}
            {showConstantsDropdown && (
              <div className={`absolute top-full right-0 mt-1 p-2 rounded-lg shadow-lg border z-50 min-w-48 ${
                preferences.theme === 'dark'
                  ? 'bg-gray-800 border-gray-600 text-white'
                  : 'bg-white border-gray-200 text-gray-900'
              }`}>
                <div className="text-xs font-medium mb-2">Mathematical Constants</div>
                <div className="space-y-1">
                  {MATHEMATICAL_CONSTANTS.map((constant) => {
                    const isVisible = constants.visibleConstants.includes(constant.id);
                    return (
                      <button
                        key={constant.id}
                        onClick={() => handleToggleConstant(constant.id)}
                        className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                          isVisible
                            ? preferences.theme === 'dark'
                              ? 'bg-blue-600 text-white'
                              : 'bg-blue-500 text-white'
                            : preferences.theme === 'dark'
                              ? 'text-gray-300 hover:bg-gray-700'
                              : 'text-gray-600 hover:bg-gray-100'
                        }`}
                        title={constant.description}
                      >
                        <span className="font-mono">{constant.symbol}</span>
                        <span className="truncate">{constant.name}</span>
                        <span className="ml-auto text-xs opacity-75">
                          {constant.value().toFixed(2)}
                        </span>
                      </button>
                    );
                  })}
                </div>
                
                {/* Master toggle */}
                <div className="border-t border-gray-300 dark:border-gray-600 mt-2 pt-2">
                  <button
                    onClick={constants.visibleConstants.length > 0 ? handleHideAllConstants : handleShowAllConstants}
                    className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                      constants.visibleConstants.length > 0
                        ? preferences.theme === 'dark'
                          ? 'bg-green-600 text-white'
                          : 'bg-green-500 text-white'
                        : preferences.theme === 'dark'
                          ? 'text-gray-300 hover:bg-gray-700'
                          : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M5 13l4 4L19 7" />
                    </svg>
                    {constants.visibleConstants.length > 0 ? 'Hide All' : 'Show All'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Separator */}
          <div className={`w-px h-6 mx-1 ${
            preferences.theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'
          }`} />

          {/* Settings Dropdown - Contains Settings and Reset All */}
          <div className="relative">
            <button
              onClick={toggleSettingsDropdown}
              className={`px-2 py-1.5 text-sm rounded transition-colors flex items-center gap-1 ${
                preferences.theme === 'dark'
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
              title="Settings and more options"
            >
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Settings</span>
              <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d={showSettingsDropdown ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
              </svg>
            </button>

            {/* Settings Dropdown Menu */}
            {showSettingsDropdown && (
              <div className={`absolute top-full right-0 mt-1 p-2 rounded-lg shadow-lg border z-50 min-w-48 ${
                preferences.theme === 'dark'
                  ? 'bg-gray-800 border-gray-600 text-white'
                  : 'bg-white border-gray-200 text-gray-900'
              }`}>
                <div className="text-xs font-medium mb-2">Settings & Actions</div>
                
                {/* Preferences */}
                <button
                  onClick={() => { handleSettings(); closeSettingsDropdown(); }}
                  className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 transition-colors ${
                    preferences.theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Settings className="w-4 h-4" />
                  Preferences
                </button>

                {/* About */}
                <button
                  onClick={() => { handleAbout(); closeSettingsDropdown(); }}
                  className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 transition-colors ${
                    preferences.theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  About
                </button>

                {/* Divider */}
                <div className={`my-2 border-t ${
                  preferences.theme === 'dark' ? 'border-gray-600' : 'border-gray-200'
                }`} />

                {/* Reset All */}
                <button
                  onClick={() => { setShowResetConfirm(true); closeSettingsDropdown(); }}
                  className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 transition-colors ${
                    preferences.theme === 'dark'
                      ? 'text-red-300 hover:bg-red-900/40'
                      : 'text-red-600 hover:bg-red-50'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m-7 0V5a2 2 0 012-2h3a2 2 0 012 2v2"
                    />
                  </svg>
                  Reset All
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Click outside to close dropdowns */}
      {(showConstantsDropdown || showSecondaryLineDropdown || showMainLineDropdown || showMobileMenu || showFileDropdown || showSettingsDropdown) && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            closeConstantsDropdown();
            closeSecondaryLineDropdown();
            closeMainLineDropdown();
            closeMobileMenu();
            closeFileDropdown();
            closeSettingsDropdown();
          }}
        />
      )}

      {/* Reset All Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div
            className={`max-w-md w-full mx-4 rounded-lg shadow-xl p-6 ${
              preferences.theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-2">Reset Canvas</h3>
            <p className="text-sm mb-4">
              This will remove all annotations, jumps, measurements, expressions, and custom ticks from the canvas.
              Your settings and preferences will be left unchanged. This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                className={`px-3 py-2 text-sm font-medium border rounded-md ${
                  preferences.theme === 'dark'
                    ? 'text-gray-200 border-gray-600 hover:bg-gray-700'
                    : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleResetAll}
                className="px-3 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
              >
                Reset All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Options Dialog - disabled for now */}
      {/* <ExportDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        onExport={handleExport}
        metadata={{
          scale: viewport.scale,
          domain: {
            start: viewport.offsetX - 500 / viewport.scale,
            end: viewport.offsetX + 500 / viewport.scale,
          },
          numberType: numberLine.numberType,
          timestamp: new Date().toISOString(),
        }}
      /> */}

      {/* Phase 3: Secondary Line Format Settings Dialog */}
      {showFormatSettingsDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowFormatSettingsDialog(false)}>
          <div 
            className={`rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto ${
              preferences.theme === 'dark'
                ? 'bg-gray-800 text-white'
                : 'bg-white text-gray-900'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-300 dark:border-gray-600 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Secondary Line Format Settings</h2>
              <button
                onClick={() => setShowFormatSettingsDialog(false)}
                className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
                  preferences.theme === 'dark'
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <NumberFormatSelector
                config={secondaryLine.numberFormat || defaultNumberFormatConfig}
                line="secondary"
                disabled={!secondaryLine.enabled}
              />
            </div>
          </div>
        </div>
      )}

      {/* Phase 3: Primary Line Format Settings Dialog */}
      {showPrimaryFormatSettingsDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowPrimaryFormatSettingsDialog(false)}>
          <div 
            className={`rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto ${
              preferences.theme === 'dark'
                ? 'bg-gray-800 text-white'
                : 'bg-white text-gray-900'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-300 dark:border-gray-600 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Primary Line Format Settings</h2>
              <button
                onClick={() => setShowPrimaryFormatSettingsDialog(false)}
                className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
                  preferences.theme === 'dark'
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <NumberFormatSelector
                config={numberLine.numberFormat || defaultNumberFormatConfig}
                line="primary"
                disabled={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderActions;